import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Badge,
  Stack,
  Paper,
  Tooltip,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useQueryClient } from "react-query";

import { NimbuxHotelMedia, VendorHotel } from "@bandido/shared/interfaces";

import { ImageList } from "@frontend/features/hotel-content-manager";
import {
  useGetHotelNimbuxHotelById,
  useGetModeratedVendorHotelsById,
  useGetVendorHotelImages,
} from "@frontend/features/hotel-content-manager/queries";
import { usePatchHotel } from "@frontend/features/hotel-content-manager/mutations";
import { LoadingSkeleton } from "@frontend/features/hotel-content-manager/components/Jobs";
import { EmptyState } from "@frontend/features/common/components/EmptyState";
import { LoadingButton } from "@mui/lab";
import { useSnackBar } from "@frontend/features/common/components/SnackBarContext";
import { QueryKeys } from "@frontend/features/common/types";

export const NimbuxHotelImageManager = () => {
  const { t } = useTranslation();
  const { hotelId } = useParams();
  const { addToast } = useSnackBar();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const upMd = useMediaQuery(theme.breakpoints.up("md"));
  const colRightWidth = upMd ? 340 : 300;

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const [hotelImages, setHotelImages] = useState<NimbuxHotelMedia[]>([]);
  const [availableImages, setAvailableImages] = useState<NimbuxHotelMedia[]>([]);
  const [selectedVendorHotel, setSelectedVendorHotel] = useState<VendorHotel>();

  const { data: vendorHotelImages, isLoading: isLoadingVendorHotelImages } = useGetVendorHotelImages(selectedVendorHotel?.id);
  const { data: nimbuxHotel, isLoading: isLoadingNimbuxHotel } = useGetHotelNimbuxHotelById(Number(hotelId));
  const { data: moderatedVendorHotels, isLoading: isLoadingModeratedVendorHotels } = useGetModeratedVendorHotelsById(Number(hotelId));
  const { mutate: patchHotel, isLoading: isUpdatingHotel } = usePatchHotel(
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.getHotelNimbuxHotelById, Number(hotelId)]);
        addToast({
          color: 'success',
          message: t('hotelContent.view.images.messages.updateSuccess'),
        });
      },
      onError: () => {
        addToast({
          color: 'error',
          message: t('hotelContent.view.images.messages.updateError'),
        });
      },
    }
  );

  const [selectedLeft, setSelectedLeft] = useState<Set<string>>(new Set());
  const [selectedRight, setSelectedRight] = useState<Set<string>>(new Set());

  const handleSelectRightImages = (url: string) => {
    setSelectedRight((prev) => {
      const next = new Set(prev);
      next.has(url) ? next.delete(url) : next.add(url);
      return next;
    });
  };

  const handleSelectLeftImages = (url: string) => {
    setSelectedLeft((prev) => {
      const next = new Set(prev);
      next.has(url) ? next.delete(url) : next.add(url);
      return next;
    });
  };

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = hotelImages?.findIndex((it) => it.order === active.id);
    const newIndex = hotelImages?.findIndex((it) => it.order === over.id);
    const reordered = arrayMove(hotelImages || [], oldIndex || 0, newIndex || 0);
    setHotelImages(reordered);
  }

  function handleMoveToAvailable() {
    const newAvailableImages = hotelImages.filter((media) => selectedLeft.has(media.url));
    setAvailableImages(newAvailableImages.concat(availableImages));
    setHotelImages(hotelImages.filter((media) => !selectedLeft.has(media.url)));
    setSelectedLeft(new Set());
    setSelectedRight(new Set(newAvailableImages.map((media) => media.url)));
  }

  function handleMoveToHotelImages() {
    const newHotelImages = availableImages.filter((media) => selectedRight.has(media.url));
    setHotelImages(newHotelImages.concat(hotelImages));
    setAvailableImages(availableImages.filter((media) => !selectedRight.has(media.url)));
    setSelectedLeft(new Set(newHotelImages.map((media) => media.url)));
    setSelectedRight(new Set());
  }

  const handleUpdateHotelImages = () => {
    patchHotel({
      id: Number(hotelId),
      medias: hotelImages.map((media, idx) => ({
        order: idx + 1,
        type: media.type,
        url: media.url,
        tagIds: [],
      })),
    });
  }

  const handleSelectVendorHotel = (id: number) => {
    setSelectedVendorHotel(moderatedVendorHotels?.find((hotel) => hotel.id === id));
    setSelectedRight(new Set());
  }

  const isLoading = isLoadingNimbuxHotel;

  useEffect(() => {
    if (vendorHotelImages) {
      setAvailableImages(vendorHotelImages.filter((media) => !nimbuxHotel?.medias.some((availableMedia) => availableMedia.url === media.url)));
    }
  }, [vendorHotelImages]);
  
  useEffect(() => {
    if (nimbuxHotel) {
      setHotelImages(nimbuxHotel.medias ?? []);
      if(availableImages.length > 0) {
        setAvailableImages(availableImages.filter((media) => !nimbuxHotel.medias.some((availableMedia) => availableMedia.url === media.url)));
      }
    }
  }, [nimbuxHotel]);

  useEffect(() => {
    if (moderatedVendorHotels) {
      setSelectedVendorHotel(moderatedVendorHotels[0]);
    }
  }, [moderatedVendorHotels]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 }, width: "calc(100vw - 480px)" }}>
      <Box display="flex" gap={2} alignItems="flex-start">
        <Box width={{ xs: "100%", md: "auto" }} gap={2}>
          <Box display="flex" gap={1} alignItems="center" p={1} justifyContent="space-between">
            <Typography variant="subtitle2" flex={1} sx={{ minWidth: 132 }}>
              {t("hotelContent.view.images.published", { count: hotelImages?.length ?? 0 })}
            </Typography>

            <Tooltip title={t("hotelContent.view.images.moveToAvailable")}>
              <span>
                <IconButton
                  color="primary"
                  onClick={handleMoveToAvailable}
                  disabled={!selectedLeft.size}
                  aria-label={t("hotelContent.view.images.moveToAvailable")}
                >
                  <ArrowForward />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <LoadingSkeleton />
            </Box>
          ) : (
          <Paper variant="outlined" sx={{ p: 1.5, width: "98%" }}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={hotelImages?.map((x) => x.order || 0) || []} strategy={rectSortingStrategy}>
                <ImageList
                  selectable
                  items={hotelImages || []}
                  selectedIds={selectedLeft}
                  onToggleSelect={(url) => handleSelectLeftImages(url)}
                  renderBadge={(img) =>
                    hotelImages?.[0]?.order === img.order ? (
                      <Badge
                        badgeContent={t("hotelContent.view.images.mainBadge")}
                        color="primary"
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                      />
                    ) : null
                  }
                  enableSorting
                />
              </SortableContext>
            </DndContext>
          </Paper>
          )}
        </Box>

        <Box width={{ xs: "100%", md: colRightWidth }} sx={{ minWidth: colRightWidth }}>
          <Stack gap={1} p={1}>
            <Box
              display="flex"
              gap={1}
              alignItems="center"
              justifyContent="space-between"
            >
              <Tooltip title={t("hotelContent.view.images.moveToPublished")}>
                <span>
                  <IconButton
                    color="primary"
                    onClick={handleMoveToHotelImages}
                    disabled={!selectedRight.size}
                    aria-label={t("hotelContent.view.images.moveToPublished")}
                  >
                    <ArrowBack />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="subtitle2" flex={1} sx={{ minWidth: 132 }}>
                {t("hotelContent.view.images.available", { count: availableImages?.length ?? 0 })}
              </Typography>
              <LoadingButton
                loading={isUpdatingHotel}
                onClick={handleUpdateHotelImages}
              >
                {t("common.save")}
              </LoadingButton>
            </Box>
            {isLoadingVendorHotelImages || isLoadingModeratedVendorHotels ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <LoadingSkeleton />
              </Box>
            ) : (
              <>
                <Select
                  fullWidth
                  variant="outlined"
                  size="small"
                  labelId="show-label"
                  label={t("hotelContent.view.images.show.label")}
                  value={selectedVendorHotel?.id}
                  onChange={(e) => handleSelectVendorHotel(e.target.value as any)}
                >
                  {moderatedVendorHotels?.map(({ id, supplierName }) => (
                    <MenuItem key={id} value={id}>{supplierName}</MenuItem>
                  ))}
                </Select>

                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  {availableImages.length > 0 ? (
                    <ImageList
                      selectable
                      singleColumn
                      items={availableImages || []}
                      selectedIds={selectedRight}
                        onToggleSelect={(url) => handleSelectRightImages(url)}
                    />
                  ) : (
                    <EmptyState
                      maxHeight="200px"
                      title={t("hotelContent.view.images.messages.noAvailableImages")}
                    />
                  )}
                </Paper>
              </>
            )}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
